import { useEffect, useState } from "react";
import { TextField, Button, Typography, Slider, Checkbox } from "@mui/material";
import MainCalculatorsList from "../MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import { CalculatorsType } from "../ConstantsEN";
import SharedHelmet from "../Helmet";
const TITLE = "Password Generator";
const DESCRIPTION = "Use our random password generator to create strong passwords for all your online accounts. Mix letters, numbers, and symbols for maximum security.";
const KEYWORDS = "Password Generator, Strong Passwords, Account Protection, Data Security, Internet Security, Random Password Generator, Letters Numbers Symbols, Personal Account Security, Create Strong Password";
const OG_URL = 'https://do-calculate.com/calculator/en/random-password-generator';
function PasswordGenerator() {
  const [passwordLength, setPasswordLength] = useState(8);
  const [includeLowercase, setIncludeLowercase] = useState(true);
  const [includeUppercase, setIncludeUppercase] = useState(true);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeSymbols, setIncludeSymbols] = useState(true);
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(true);
  const [excludeBrackets, setExcludeBrackets] = useState(true);
  const [noRepeatedChars, setNoRepeatedChars] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState("");
  const [passwordEntropy, setPasswordEntropy] = useState(0);
  const [error, setError] = useState("");
  useEffect(() => {
    generatePassword();
  }, [
    passwordLength,
    includeLowercase,
    includeUppercase,
    includeNumbers,
    includeSymbols,
    excludeAmbiguous,
    excludeBrackets,
    noRepeatedChars,
  ]);
  useEffect(() => {
    calculatePasswordEntropy();
    evaluatePasswordStrength();
  }, [generatedPassword]);
  const calculatePasswordEntropy = () => {
    let charsetSize = 0;
    const excludedChars = excludeAmbiguous ? "iIl1L|o0O'\";:., " : "";
    const excludedBrackets = excludeBrackets ? "[]<>()" : "";
    if (includeLowercase) {
      charsetSize += 26;
    }
    if (includeUppercase) {
      charsetSize += 26;
    }
    if (includeNumbers) {
      charsetSize += 10;
    }
    if (includeSymbols) {
      const allowedSymbols = "!@#$%^&*_+=-|~";
      const filteredSymbols = allowedSymbols
        .split("")
        .filter(
          (symbol) =>
            !excludedChars.includes(symbol) &&
            !excludedBrackets.includes(symbol)
        );
      charsetSize += filteredSymbols.length;
    }
    const patternEntropy = calculatePatternEntropy();
    const passwordEntropy = Math.log2(Math.pow(charsetSize, passwordLength));
    const repeatedCharacterEntropy = calculateRepeatedCharacterEntropy();
    setPasswordEntropy(
      Math.max(
        passwordEntropy + patternEntropy + repeatedCharacterEntropy,
        0
      ).toFixed(1)
    );
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const calculatePatternEntropy = () => {
    const patterns = [
      /\d{2,}/,
      /[a-z]{2,}/,
      /[A-Z]{2,}/,
      /[^a-zA-Z0-9]{2,}/,
    ];
    let patternEntropy = 0;
    patterns.forEach((pattern) => {
      const matches = generatedPassword.match(pattern);
      if (matches) {
        const patternLength = matches.reduce(
          (total, match) => total + match.length,
          0
        );
        patternEntropy += Math.log2(patternLength);
      }
    });
    return -patternEntropy;
  };
  const calculateRepeatedCharacterEntropy = () => {
    const repeatedCharacters = generatedPassword.match(/(.)\1+/g);
    if (repeatedCharacters) {
      const repeatedCharactersCount = repeatedCharacters.reduce(
        (total, match) => total + match.length - 1,
        0
      );
      const uniqueCharacterCount = passwordLength - repeatedCharactersCount;
      return Math.log2(uniqueCharacterCount);
    }
    return 0;
  };
  const evaluatePasswordStrength = () => {
    if (passwordEntropy === 0) {
      setPasswordStrength("");
    } else if (passwordEntropy < 28) {
      setPasswordStrength("Very Weak");
    } else if (passwordEntropy < 36) {
      setPasswordStrength("Weak");
    } else if (passwordEntropy < 60) {
      setPasswordStrength("Moderate");
    } else if (passwordEntropy < 128) {
      setPasswordStrength("Strong");
    } else {
      setPasswordStrength("Very Strong");
    }
  };
  const generatePassword = () => {
    let charset = "";
    if (includeLowercase) charset += "abcdefghijklmnopqrstuvwxyz";
    if (includeUppercase) charset += "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    if (includeNumbers) charset += "0123456789";
    if (includeSymbols) charset += "!@#$%^&*()_+-=[]{}|;:,.<>?";
    if (excludeAmbiguous) {
      charset = charset.replace(/[iIl1L|o0O`'";:.,]/g, "");
    }
    if (excludeBrackets) {
      charset = charset.replace(/[(){}[\]<>]/g, "");
    }
    if (charset.length === 0) {
      setError(
        "Please include at least one character set for the password to be generated."
      );
      return;
    }
    if (noRepeatedChars && passwordLength > charset.length) {
      setError(
        "Not enough characters to create this password. Please include more character sets."
      );
      return;
    }
    let generatedPassword = "";
    for (let i = 0; i < passwordLength; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      generatedPassword += charset.charAt(randomIndex);
      charset = charset.replace(charset.charAt(randomIndex), "");
    }
    setGeneratedPassword(generatedPassword);
    setError("");
  };
  const handleSliderChange = (event, value) => {
    setPasswordLength(value);
  };
  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    switch (name) {
      case "includeLowercase":
        setIncludeLowercase(checked);
        break;
      case "includeUppercase":
        setIncludeUppercase(checked);
        break;
      case "includeNumbers":
        setIncludeNumbers(checked);
        break;
      case "includeSymbols":
        setIncludeSymbols(checked);
        break;
      case "excludeAmbiguous":
        setExcludeAmbiguous(checked);
        break;
      case "excludeBrackets":
        setExcludeBrackets(checked);
        break;
      case "noRepeatedChars":
        setNoRepeatedChars(checked);
        break;
      default:
        break;
    }
  };
  return (
    <div className="ltr">
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "32px" }}
          >
            <h1 style={{ fontSize: "14px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  A password generator is a tool used to create random and strong passwords. The purpose of using this tool is to enhance the security of your online accounts, as strong passwords can be difficult to predict and break.
                </p>
                <p>
                  This tool can generate secure, strong, and random passwords. To ensure security, the password is generated entirely on the web page without being sent over the internet.
                </p>
                <p>
                  Benefits of using a password generator:
                  <ul>
                    <li>Protection against hacking: By using strong and random passwords, protection against hacking attempts and account cloning is enhanced.</li>
                    <li>Securing sensitive data: Reducing the likelihood of leaking your sensitive data online when using hard-to-predict passwords.</li>
                    <li>Ease of recall: The password generator automatically creates passwords, reducing the burden of remembering complex passwords.</li>
                  </ul>
                </p>
                <hr />
              </div>
              <br />
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7">
                {generatedPassword && !error && (
                  <div className="mb-2">
                    <Typography
                      className="ltr"
                      fontSize="20px"
                      fontWeight="bold"
                      sx={{ wordWrap: "break-word", textAlign: "center" }}
                    >
                      {generatedPassword}
                    </Typography>
                    <Typography fontSize="18px">
                      Password Strength:{" "}
                      <span
                        style={{
                          color:
                            passwordStrength === "Very Weak"
                              ? "rgb(255, 0, 0)"
                              : passwordStrength === "Weak"
                                ? "rgb(255, 102, 0)"
                                : passwordStrength === "Moderate"
                                  ? "rgb(102, 204, 102)"
                                  : passwordStrength === "Strong"
                                    ? "rgb(51, 204, 51)"
                                    : "rgb(0, 153, 0)",
                        }}
                      >
                        {passwordStrength}
                      </span>
                    </Typography>
                    <Typography fontSize="18px" sx={{ mb: "15px" }}>
                      Password Entropy: {passwordEntropy} bits
                    </Typography>
                    <div className="d-flex">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        viewBox="0 0 24 24"
                        width="24"
                        fill="currentColor"
                        style={{ marginLeft: "5px" }}
                      >
                        <path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                      </svg>
                      <Typography
                        onClick={handleCopy}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "rgb(102, 204, 102)",
                          textDecoration: "underline",
                          fontSize: "18px",
                          width: "fit-content",
                          "&:hover": {
                            color: "rgb(23 158 23)",
                            textDecoration: "none",
                            cursor: "pointer",
                          },
                        }}
                      >
                        Copy Password
                      </Typography>

                    </div>
                  </div>
                )}
                {error && (
                  <Typography
                    variant="body2"
                    color="error"
                    fontSize="16px"
                    sx={{ my: 2 }}
                  >
                    {error}
                  </Typography>
                )}
              </div>
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div
                  className="mb-3 mt-2 d-flex"
                  style={{ alignItems: "center" }}
                >
                  <label>Password Length:</label>
                  <TextField
                    value={passwordLength}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (value !== "" && (value < 8 || value > 50)) {
                        value = Math.min(Math.max(value, 8), 50);
                      }
                      setPasswordLength(value);
                    }}
                    sx={{
                      width: "150px",
                      mr: "15px",
                      bgcolor: "#f9f9f9",
                      "&  .MuiFormHelperText-root.Mui-error": {
                        backgroundColor: "rgb(229, 230, 227) !important",
                        margin: 0,
                      },
                    }}
                    type="number"
                    InputProps={{
                      inputProps: {
                        min: 8,
                        max: 50,
                      },
                    }}
                    size="small"
                    color="success"
                  />
                </div>
                <div className="mb-2" style={{ width: "90%" }}>
                  <Slider
                    value={passwordLength}
                    onChange={handleSliderChange}
                    color="success"
                    sx={{ marginRight: "100px" }}
                    min={8}
                    max={50}
                    step={1}
                    marks={[
                      { value: 8, label: "8" },
                      { value: 20, label: "20" },
                      { value: 35, label: "35" },
                      { value: 50, label: "50" },
                    ]}
                  />
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={includeLowercase}
                    onChange={handleCheckboxChange}
                    name="includeLowercase"
                    color="success"
                  />
                  Include lowercase characters (a-z)
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={includeUppercase}
                    onChange={handleCheckboxChange}
                    name="includeUppercase"
                    color="success"
                  />
                  "Include uppercase characters (A-Z)
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={includeNumbers}
                    onChange={handleCheckboxChange}
                    name="includeNumbers"
                    color="success"
                  />
                  Include numbers  (0-9)
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={includeSymbols}
                    onChange={handleCheckboxChange}
                    name="includeSymbols"
                    color="success"
                  />
                  {`Include symbols (~!@#$%^&*()_+-=:;){/}[]?><)`}
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={excludeAmbiguous}
                    onChange={handleCheckboxChange}
                    name="excludeAmbiguous"
                    color="success"
                  />
                  {`Exclude ambiguous characters (iIl1L|o0O'";:.,)`}
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={excludeBrackets}
                    onChange={handleCheckboxChange}
                    name="excludeBrackets"
                    color="success"
                  />
                  {`Exclude parentheses ({}، []، <>, ())`}
                </div>
                <div className="mb-2">
                  <Checkbox
                    checked={noRepeatedChars}
                    onChange={handleCheckboxChange}
                    name="noRepeatedChars"
                    color="success"
                  />
                  No repeated characters
                </div>
                <div className="text-center">
                  <Button
                    variant="contained"
                    color="success"
                    onClick={generatePassword}
                  >
                    Generate Password
                  </Button>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList
            CalculatorsTypeId={CalculatorsType.otherCalculators}
          ></MainCalculatorsList>
          <br />
        </div>
      </div>
      <div className="row" style={{ height: "auto" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                Everything You Need to Know About Passwords:
              </div>
              <div className="info-section-body">
                <p>
                  A password is usually a sequence of characters that may include
                  letters, numbers, and symbols used to access something, typically an
                  account, and prevent others from accessing it. In the current era of
                  the internet, most people likely have experience obtaining passwords
                  for various accounts. Therefore, it is essential to understand how to
                  create a strong password (or use a password generator) and take
                  necessary measures to protect it.
                </p>
                <hr />
                <u>
                  <h2 style={{ fontSize: "16px" }}>Password Strength:</h2>
                </u>
                <br />
                <p>
                  Password strength is a measure of how effective a password is against
                  guessing or hacker attacks. Generally, password strength is an
                  estimate of the average number of attempts required for someone to
                  successfully guess the password. This is influenced by the length,
                  complexity, and unpredictability of the password. For example, if a
                  password includes someone's name, birthdate, or other easily
                  discoverable personal information, it is likely to be weak.
                  Regardless of the actual password, proper security controls play a
                  crucial role in reducing the risk of security breaches. Security
                  controls include measures like two-step authentication for your
                  password, or locking the account for a period after a certain number
                  of failed attempts.
                </p>
                <hr />
                <u>
                  <h2 style={{ fontSize: "16px" }}>
                    How to Create a Secure Password:
                  </h2>
                </u>
                <br />
                <p>
                  Creating a secure password involves following certain rules designed
                  to increase password security. This largely includes creating a
                  strong password but also involves aspects such as changing the
                  password regularly and being aware of common passwords to avoid using
                  them (e.g., "password," "123456," "qwert..."). Here are some rules
                  that can be used to create a strong password:
                  <br />
                  - Include lowercase letters [a-z]
                  <br />
                  - Include uppercase letters [A-Z]
                  <br />
                  - Include numbers [0-9]
                  <br />
                  - Include symbols [!@#$%^&*()...]
                  <br />
                  - Exclude words containing personal information
                  <br />
                  - Exclude words found in blacklists for passwords – there are
                  online blacklists for passwords, or a company/organization may have
                  its own blacklists
                  <br />
                  - Exclude the company/organization name and abbreviations
                  <br />
                  - Exclude passwords that match common formats, such as calendar
                  dates, license plate numbers, phone numbers, or other common number
                  formats
                  <br />
                  Additionally, many password policies usually include a minimum
                  password length because, in general, the longer the password and the
                  more diverse the types of characters, the more secure the password.
                </p>
                <hr />
                <u>
                  <h2 style={{ fontSize: "16px" }}>Password Entropy:</h2>
                </u>
                <br />
                <p>
                  The password generator also determines password entropy, measured in
                  bits. The higher the entropy, the more challenging it becomes to
                  guess the password. In the context of hacker brute-force attacks
                  (where all possibilities are tested), a password entropy of 100 bits
                  would require 2^100 attempts to exhaust all possibilities. However,
                  on average, nearly half of these possibilities would need to be
                  exhausted before finding the correct option.
                </p>
                <hr />
                <u>
                  <h2 style={{ fontSize: "16px" }}>
                    How to Protect Your Password:
                  </h2>
                </u>
                <br />
                <p>
                  Protecting your password is equally important as having a strong
                  password. Here are some suggested measures to help protect your
                  password. There may be other actions anyone can take, but here are
                  some guidelines that may help:
                  <br />
                  - Never share your password with others – Ideally, you should be
                  the only person who knows your password. Even if you trust the
                  person you're sharing your password with and they have no malicious
                  intentions, they might not be as cautious about protecting your
                  password. The more people who know your password, the higher the
                  risk of someone else accessing your account.
                  <br />
                  - Do not use the same password across different websites and
                  accounts – While it might be convenient to use the same password
                  across different websites and accounts, it is not advisable.
                  Having the same password for all your accounts, regardless of how
                  strong the password is, could mean that a security breach in one
                  account jeopardizes the security of all your accounts. Using a
                  password manager can help you manage your passwords more easily
                  and increase security by allowing you to use different passwords
                  for different accounts.
                  <br />
                  - Regularly change your passwords – While this is another
                  inconvenient measure, it theoretically helps maintain the
                  security of your accounts. This helps in cases where someone might
                  know your password, can access it, but might not immediately
                  attempt to do something harmful. In these cases, changing your
                  password would limit the time they have access to your account,
                  assuming they can't determine the new password immediately. In
                  cases where you've stored your password on an old device you may
                  have sold or discarded, changing your password would disable
                  anyone from using the stored password.
                  <br />
                  - Never save your passwords on public devices – Ideally, avoid
                  saving passwords altogether to reduce the risk of unwanted access
                  to your accounts. Similarly, be cautious about accessing
                  sensitive accounts on insecure public networks.
                  <br />
                  - Do not keep clear lists of your passwords accessible to
                  others, whether physical or electronic – For example, a sticky
                  note on your desk with accounts and passwords, a Word document on
                  your desktop named "Passwords" with information for all your
                  accounts, or a note on your mobile phone could expose you to
                  unnecessary risk of losing your password. As mentioned earlier,
                  consider using a password manager instead. Phones and laptops can
                  be lost or stolen. Ideally, a person should use a password manager
                  or be able to remember their passwords for different accounts
                  based on the specific account.
                </p>
                <hr />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default PasswordGenerator;
